import type { ThemeConfig } from './types';
import { loadWebFont, resolveFontStack } from './fonts';

/** Body text size before a theme sets one; matches Tailwind's text-sm. */
export const DEFAULT_BODY_FONT_SIZE = '14px';
/** Heading size before a theme sets one; matches Tailwind's text-2xl. */
export const DEFAULT_HEADING_FONT_SIZE = '24px';

// The theme is stored as the builder sent it — nothing on the backend validates
// these fields — so each one is checked here before it reaches a style property.
// A value outside the range is not clamped but dropped for the default: an
// out-of-range size is a broken theme, not a preference to honour approximately.
const PX_SIZE = /^(\d{1,3}(?:\.\d+)?)px$/;

export function resolveFontSize(value: string | undefined, fallback: string, min: number, max: number): string {
  const match = PX_SIZE.exec((value ?? '').trim());
  if (!match) return fallback;
  const px = Number(match[1]);
  return px >= min && px <= max ? `${px}px` : fallback;
}

/**
 * The CSS `background-image` value for a theme's image URL, or `none`. Only an
 * absolute http(s) URL is accepted, and it is written as a quoted `url("…")` with
 * the characters that could end that string escaped, so a URL can only ever be a
 * URL — never the rest of a declaration.
 */
export function resolveBackgroundImage(url: string | undefined): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return 'none';
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'none';
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return 'none';
  const escaped = parsed.href.replace(/["\\\n\r\f]/g, (c) => `\\${c.charCodeAt(0).toString(16)} `);
  return `url("${escaped}")`;
}

export function applyTheme(theme: ThemeConfig): void {
  const root = document.documentElement;
  root.style.setProperty('--3sb-primary', theme.primaryColour);
  root.style.setProperty('--3sb-button', theme.buttonColour?.trim() || theme.primaryColour);
  root.style.setProperty('--3sb-bg', theme.backgroundColour);
  // A full stack, never the bare family name: nothing else follows --3sb-font,
  // so an unloadable family here means the form renders in the browser default.
  root.style.setProperty('--3sb-font', resolveFontStack(theme.fontFamily));
  const headingFamily = theme.headingFontFamily?.trim() || theme.fontFamily;
  root.style.setProperty('--3sb-heading-font', resolveFontStack(headingFamily));
  root.style.setProperty(
    '--3sb-body-size',
    resolveFontSize(theme.bodyFontSize, DEFAULT_BODY_FONT_SIZE, 10, 32),
  );
  root.style.setProperty(
    '--3sb-heading-size',
    resolveFontSize(theme.headingFontSize, DEFAULT_HEADING_FONT_SIZE, 12, 64),
  );
  root.style.setProperty('--3sb-radius', theme.borderRadius);
  loadWebFont(theme.fontFamily, headingFamily);

  // With an image behind it the form sits on a card of the background colour,
  // so its text keeps the contrast the author chose whatever the photo is.
  const backgroundImage = resolveBackgroundImage(theme.backgroundImageUrl);
  root.style.setProperty('--3sb-bg-image', backgroundImage);
  root.toggleAttribute('data-3sb-bg-image', backgroundImage !== 'none');

  if (theme.customCss) {
    // customCss is trusted tenant-supplied config; the backend must enforce ownership
    // before persisting it. It is written directly to a style tag without sanitisation.
    let styleEl = document.querySelector('[data-3sb-custom]') as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.setAttribute('data-3sb-custom', '');
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = theme.customCss;
  }
}
